"use client";

import { motion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";

interface AnimatedMessageProps {
  text: string;
  speed?: number; // milliseconds between words
  className?: string;
  isLoading?: boolean; // loading state
  skipAnimation?: boolean; // if true, display all text immediately without animation
}

export function AnimatedMessage({
  text,
  speed = 200,
  className = "",
  isLoading = false,
  skipAnimation = false,
}: AnimatedMessageProps) {
  const [displayedWords, setDisplayedWords] = useState<string[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const previousWordCountRef = useRef(0);

  const words = useMemo(
    () => text.split(" ").filter((word) => word.trim() !== ""),
    [text]
  );

  // Reset animation when text changes
  useEffect(() => {
    if (skipAnimation) {
      // If skipAnimation is true, display all words immediately
      setDisplayedWords(words);
      setCurrentIndex(words.length);
      setIsAnimating(false);
      previousWordCountRef.current = words.length;
    } else {
      setDisplayedWords([]);
      setCurrentIndex(0);
      setIsAnimating(true);
      previousWordCountRef.current = 0;
    }
  }, [text, skipAnimation, words]);

  useEffect(() => {
    if (!isAnimating) return;

    if (currentIndex < words.length) {
      const timer = setTimeout(() => {
        setDisplayedWords((prev) => [...prev, words[currentIndex]]);
        setCurrentIndex((prev) => prev + 1);
      }, speed);

      return () => clearTimeout(timer);
    } else {
      setIsAnimating(false);
    }
  }, [currentIndex, words, speed, isAnimating]);

  // Track word count for animation
  useEffect(() => {
    if (displayedWords.length > previousWordCountRef.current) {
      previousWordCountRef.current = displayedWords.length;
    } else if (displayedWords.length === 0) {
      previousWordCountRef.current = 0;
    }
  }, [displayedWords.length]);

  // If no text, return empty div
  if (!text || text.trim() === "") {
    return <div className={className} />;
  }

  return (
    <div ref={containerRef} className={`relative overflow-hidden ${className}`}>
      {/* Top gradient overlay */}
      {/* <div className="pointer-events-none absolute left-0 right-0 top-0 z-10 h-6 bg-gradient-to-b from-white to-transparent" /> */}

      {/* Bottom gradient overlay */}
      {/* <div className="pointer-events-none absolute bottom-0 left-0 right-0 z-10 h-6 bg-gradient-to-t from-white to-transparent" /> */}

      <div className="px-4 text-center">
        <div
          className="flex flex-wrap justify-start items-start gap-1"
          style={{
            lineHeight: "32px",
            fontFamily: "Roboto, sans-serif",
          }}
        >
          {displayedWords.map((word, index) => {
            const isNewWord =
              index === displayedWords.length - 1 &&
              displayedWords.length > previousWordCountRef.current;
            const loadingOpacity = isLoading ? 1 : 1;
            const loadingColor = isLoading ? "#9ca3af" : "#1f2937";

            return (
              <span key={`word-${index}-${word}`}>
                {isNewWord ? (
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{
                      opacity: loadingOpacity,
                      color: loadingColor,
                    }}
                    transition={{
                      duration: 0.15,
                      ease: "easeOut",
                    }}
                    style={{
                      fontSize: "20px",
                    }}
                    className="font-normal"
                  >
                    {word}
                  </motion.span>
                ) : (
                  <span
                    style={{
                      opacity: loadingOpacity,
                      color: loadingColor,
                      fontSize: "20px",
                    }}
                    className="font-normal"
                  >
                    {word}
                  </span>
                )}
                {index < displayedWords.length - 1 ? " " : ""}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default AnimatedMessage;
